import { format } from "date-fns";
import { collection, doc, type Firestore, getDoc, runTransaction } from "firebase/firestore";
import { formatDate2 } from "./formatters";
import {
  calcularGastosDelDia,
  type GastoRutaCompatible,
  obtenerGastosDelDiaDeRuta,
} from "./rutaGastos";
import { calcularSaldoActualRuta, obtenerIngresosRetirosDelDiaDeRuta } from "./rutaMovimientos";

export type OrigenCierreCaja = "manual" | "automatico";

export type GastoCierreCajaCompatible = GastoRutaCompatible;

export type ClienteCierreCajaCompatible = {
  createdAt?: any;
  fechaActual?: string;
  uid?: string;
};

export type UsuarioRutaCierreCajaCompatible = {
  adminUid?: string;
  historialGastos?: GastoCierreCajaCompatible[];
  movimientosCount?: number | string;
  nombreRuta?: string;
  saldoInicial?: number | string;
  totalIngresos?: number | string;
  totalSalidas?: number | string;
  uid?: string;
  ultimoMovimientoAt?: any;
};

export type CierreCajaCompatible = {
  adminUid?: string;
  cobrosDelDia?: number | string;
  clientesNuevosDelDia?: number | string;
  cerradoPor?: string;
  fechaClave?: string;
  fechaHoraGenerado?: any;
  gastosDelDia?: number | string;
  ingresosDelDia?: number | string;
  nombreRuta?: string;
  origen?: OrigenCierreCaja;
  prestamosDelDia?: number | string;
  retirosDelDia?: number | string;
  rutaUid?: string;
  saldoActual?: number | string;
};

export type GuardarCierreCajaParams = {
  cerradoPor?: string | null;
  clientes: ClienteCierreCajaCompatible[] | null;
  cobrosDelDia: number;
  db: Firestore;
  fechaEvento?: Date;
  origen: OrigenCierreCaja;
  prestamosDelDia: number;
  usuarioRuta: UsuarioRutaCierreCajaCompatible | null;
};

export type GuardarCierreCajaResult = {
  cierre: CierreCajaCompatible;
  created: boolean;
};

const normalizarNumero = (value: number | string | null | undefined): number => {
  const parsedValue = Number(value ?? 0);

  return Number.isNaN(parsedValue) ? 0 : parsedValue;
};

const normalizarFecha = (fecha: any): Date | null => {
  if (fecha instanceof Date && !Number.isNaN(fecha.getTime())) {
    return fecha;
  }

  if (typeof fecha?.toDate === "function") {
    const fechaConvertida = fecha.toDate();

    return fechaConvertida instanceof Date && !Number.isNaN(fechaConvertida.getTime())
      ? fechaConvertida
      : null;
  }

  const fechaConvertida = new Date(fecha);

  return Number.isNaN(fechaConvertida.getTime()) ? null : fechaConvertida;
};

export const crearFechaClaveCierreCaja = (fecha: Date = new Date()): string =>
  format(fecha, "yyyy-MM-dd");

const esClienteNuevoEnFecha = (
  cliente: ClienteCierreCajaCompatible,
  fechaReferencia: Date,
): boolean => {
  const createdAt = normalizarFecha(cliente.createdAt);

  if (createdAt) {
    return crearFechaClaveCierreCaja(createdAt) === crearFechaClaveCierreCaja(fechaReferencia);
  }

  const fechaActualCliente = String(cliente.fechaActual ?? "").trim();

  return fechaActualCliente.length > 0 && fechaActualCliente === formatDate2(fechaReferencia);
};

export const calcularClientesNuevosDelDia = (
  clientes: ClienteCierreCajaCompatible[] | null,
  fechaReferencia: Date = new Date(),
): number => {
  if (!clientes?.length) {
    return 0;
  }

  return clientes.reduce((total, cliente) => {
    return esClienteNuevoEnFecha(cliente, fechaReferencia) ? total + 1 : total;
  }, 0);
};

export const calcularSaldoActualCierreCaja = (
  usuarioRuta: UsuarioRutaCierreCajaCompatible | null,
): number => {
  return calcularSaldoActualRuta(usuarioRuta);
};

export const crearCierresCajaCollectionRef = (db: Firestore, adminUid: string, rutaUid: string) =>
  collection(db, "admin_users", adminUid, "rutas", rutaUid, "cierresDeCaja");

export const crearCierreCajaRef = (
  db: Firestore,
  adminUid: string,
  rutaUid: string,
  fechaClave: string,
) => doc(crearCierresCajaCollectionRef(db, adminUid, rutaUid), fechaClave);

export const construirCierreCajaPayload = ({
  cerradoPor,
  clientes,
  cobrosDelDia,
  fechaEvento = new Date(),
  gastosDelDia,
  ingresosDelDia,
  origen,
  prestamosDelDia,
  retirosDelDia,
  usuarioRuta,
}: Omit<GuardarCierreCajaParams, "db"> & {
  gastosDelDia: number;
  ingresosDelDia: number;
  retirosDelDia: number;
}): CierreCajaCompatible => ({
  adminUid: usuarioRuta?.adminUid ?? "",
  cobrosDelDia: normalizarNumero(cobrosDelDia),
  clientesNuevosDelDia: calcularClientesNuevosDelDia(clientes, fechaEvento),
  ...(cerradoPor ? { cerradoPor } : {}),
  fechaClave: crearFechaClaveCierreCaja(fechaEvento),
  fechaHoraGenerado: fechaEvento,
  gastosDelDia: normalizarNumero(gastosDelDia),
  ingresosDelDia: normalizarNumero(ingresosDelDia),
  nombreRuta: usuarioRuta?.nombreRuta ?? "",
  origen,
  prestamosDelDia: normalizarNumero(prestamosDelDia),
  retirosDelDia: normalizarNumero(retirosDelDia),
  rutaUid: usuarioRuta?.uid ?? "",
  saldoActual: calcularSaldoActualCierreCaja(usuarioRuta),
});

export const guardarCierreCaja = async ({
  cerradoPor,
  clientes,
  cobrosDelDia,
  db,
  fechaEvento = new Date(),
  origen,
  prestamosDelDia,
  usuarioRuta,
}: GuardarCierreCajaParams): Promise<GuardarCierreCajaResult> => {
  if (!usuarioRuta?.adminUid || !usuarioRuta?.uid) {
    throw new Error("No se encontro la informacion de la ruta");
  }

  const fechaClave = crearFechaClaveCierreCaja(fechaEvento);
  const cierreRef = crearCierreCajaRef(db, usuarioRuta.adminUid, usuarioRuta.uid, fechaClave);
  const cierreExistenteSnapshot = await getDoc(cierreRef);

  if (cierreExistenteSnapshot.exists()) {
    return {
      cierre: cierreExistenteSnapshot.data() as CierreCajaCompatible,
      created: false,
    };
  }

  const gastosLegacyDelDia = calcularGastosDelDia(usuarioRuta?.historialGastos, fechaEvento);
  const gastosRemotosDelDia = await obtenerGastosDelDiaDeRuta({
    adminUid: usuarioRuta.adminUid,
    db,
    fechaReferencia: fechaEvento,
    rutaUid: usuarioRuta.uid,
  });
  const gastosDelDia = gastosRemotosDelDia > 0 ? gastosRemotosDelDia : gastosLegacyDelDia;
  const metricasAdminDelDia = await obtenerIngresosRetirosDelDiaDeRuta({
    adminUid: usuarioRuta.adminUid,
    db,
    fechaReferencia: fechaEvento,
    rutaUid: usuarioRuta.uid,
  });
  const ingresosDelDia = metricasAdminDelDia.ingresosDelDia;
  const retirosDelDia = metricasAdminDelDia.retirosDelDia;

  const cierre = construirCierreCajaPayload({
    cerradoPor,
    clientes,
    cobrosDelDia,
    fechaEvento,
    gastosDelDia,
    ingresosDelDia,
    origen,
    prestamosDelDia,
    retirosDelDia,
    usuarioRuta,
  });

  return runTransaction(db, async (transaction) => {
    const cierreSnapshot = await transaction.get(cierreRef);

    if (cierreSnapshot.exists()) {
      return {
        cierre: cierreSnapshot.data() as CierreCajaCompatible,
        created: false,
      };
    }

    transaction.set(cierreRef, cierre);

    return {
      cierre,
      created: true,
    };
  });
};
